import React from 'react';

// タグの item 選択 UI（react-select）で、subgroup を見出し（grouped options）として扱うための共通部品。
// 管理画面の TagSelectionEditor と公開加盟店申込フォーム（MerchantApplicationForm）の双方から利用する。
// 付与対象は常に葉 item のため、subgroup は「見出し・検索・値表示」にのみ使い、選択値は item のまま。

// item Select 用オプション。選択後の値表示・検索に subgroup 名を使う。
export type ItemOption = { label: string; value: string; subgroupName: string | null };
export type ItemOptionGroup = { label: string; options: ItemOption[] };

// grouped options 化の入力。呼び出し側で自身のデータ形状から正規化して渡す。
export type GroupedItemInput = {
  id: string;
  name: string;
  // 所属 subgroup id。未分類（グループ直下）は null。
  subgroupId: string | null;
  // 所属 subgroup の表示名（見つからなければ null）。
  subgroupName: string | null;
};
export type SubgroupMeta = { id: string; name: string };

export const UNGROUPED_LABEL = '未分類';

const toItemOption = (item: GroupedItemInput): ItemOption => ({
  label: item.name,
  value: item.id,
  subgroupName: item.subgroupName,
});

// subgroup があれば見出し付き（grouped options）、無ければフラット。表示順は subgroups（作成順）に従い、
// 末尾に「未分類」（どの active subgroup にも属さない item）を置く。value 照合には flat を使う。
// subgroups に存在しない id を指す item（orphan）も未分類に集約し、メニューから取りこぼさない。
export const buildItemSelectOptions = (
  items: GroupedItemInput[],
  subgroups: SubgroupMeta[]
): { options: ItemOption[] | ItemOptionGroup[]; flat: ItemOption[] } => {
  const flat = items.map(toItemOption);
  if (subgroups.length === 0) {
    return { options: flat, flat };
  }
  const knownSubgroupIds = new Set(subgroups.map((sg) => sg.id));
  const grouped: ItemOptionGroup[] = subgroups
    .map((sg) => ({
      label: sg.name,
      options: items.filter((it) => it.subgroupId === sg.id).map(toItemOption),
    }))
    .filter((g) => g.options.length > 0);
  const ungrouped = items
    .filter((it) => it.subgroupId === null || !knownSubgroupIds.has(it.subgroupId))
    .map(toItemOption);
  const options: ItemOptionGroup[] =
    ungrouped.length > 0 ? [...grouped, { label: UNGROUPED_LABEL, options: ungrouped }] : grouped;
  return { options, flat };
};

// item 名に加えて subgroup 見出し名でもインクリメンタル検索できるようにする。
export const filterItemOption = (
  candidate: { label: string; value: string; data: ItemOption },
  rawInput: string
): boolean => {
  const input = rawInput.trim().toLowerCase();
  if (!input) return true;
  const { label, data } = candidate;
  return (
    label.toLowerCase().includes(input) ||
    (!!data.subgroupName && data.subgroupName.toLowerCase().includes(input))
  );
};

// 選択後の値表示（単一選択の値・複数選択のチップ）では所属 subgroup を併記する。
// ドロップダウン内（context==='menu'）は subgroup 見出しで既にグルーピング済みのため item 名のみ。
export const formatItemOptionLabel = (
  option: ItemOption,
  meta: { context: 'menu' | 'value' }
): React.ReactNode =>
  meta.context === 'value' && option.subgroupName
    ? `${option.subgroupName} / ${option.label}`
    : option.label;
